import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { X, Loader2, Trophy, RotateCcw, BookOpen } from "lucide-react";
import client from "@/lib/api";
import { useTts } from "@/hooks/useTts";
import { StepView, flatten } from "@/pages/Lesson";
import { useLang } from "@/context/LangContext";

const PASS = 70;

export default function ModuleReview() {
  const { id } = useParams();
  const nav = useNavigate();
  const { play, stop, playing, loading, error: ttsError } = useTts();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [screens, setScreens] = useState([]);
  const [idx, setIdx] = useState(0);
  const [done, setDone] = useState(false);
  const [answered, setAnswered] = useState(0);
  const [correct, setCorrect] = useState(0);
  const missedRef = useRef([]);

  const load = () => client.get(`/learn/module-review/${id}`).then(({ data }) => {
    setData(data); setScreens(flatten(data.steps)); setIdx(0); setDone(false);
    setAnswered(0); setCorrect(0); missedRef.current = [];
  }).catch(() => { nav("/learn"); });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, nav]);
  useEffect(() => { stop(); }, [idx, stop]);

  const grade = (gstep, ok) => {
    setAnswered((a) => a + 1);
    if (ok) setCorrect((c) => c + 1);
    else if (gstep?.word) missedRef.current.push(gstep.word);
  };

  const score = answered ? Math.round((correct / answered) * 100) : 100;
  const passed = score >= PASS;

  const finish = () => {
    const sc = answered ? Math.round((correct / answered) * 100) : 100;
    client.post(`/learn/module-checkpoint/${id}`, {
      score: sc, total: answered, passed: sc >= PASS, missed_words: missedRef.current,
    }).catch(() => {});
    setDone(true);
    window.dispatchEvent(new Event("mel:progress-refresh"));
  };

  if (!data) return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><Loader2 className="animate-spin text-[#2563EB]" /></div>;

  if (done) return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center px-6 text-center">
      <div className={`w-24 h-24 rounded-full flex items-center justify-center shadow-lg ${passed ? "bg-gradient-to-br from-[#2563EB] to-[#3B82F6]" : "bg-amber-500"}`} data-testid="checkpoint-result">
        <Trophy className="text-white" size={44} />
      </div>
      <h1 className="font-heading text-2xl font-extrabold text-[#0F172A] mt-6">{passed ? t("cp_passed") : t("cp_keep_going")}</h1>
      <p className="text-slate-500 mt-1">{t("cp_score", { s: score })}</p>
      <p className="text-slate-400 text-sm mt-1">{t("mr_reviewed", { n: answered })}</p>
      {!passed && (
        <button onClick={load} data-testid="checkpoint-retry" className="w-full max-w-[320px] mt-7 bg-[#2563EB] text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2"><RotateCcw size={18} /> {t("cp_try_again")}</button>
      )}
      <button onClick={() => nav("/learn")} data-testid="module-review-done" className={`w-full max-w-[320px] ${passed ? "mt-7 bg-[#2563EB] text-white" : "mt-3 border border-[#2563EB]/30 bg-[#2563EB]/5 text-[#2563EB]"} font-bold py-4 rounded-2xl flex items-center justify-center gap-2`}>
        {!passed && <BookOpen size={18} />}{t("common_back_to_learn")}
      </button>
    </div>
  );

  const step = screens[idx];
  const progress = screens.length ? Math.round((idx / screens.length) * 100) : 0;
  const next = () => { if (idx + 1 >= screens.length) finish(); else setIdx((i) => i + 1); };
  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <div className="flex items-center gap-3 px-4 pt-6 pb-3">
        <button onClick={() => nav("/learn")} data-testid="mr-quit"><X size={22} className="text-slate-400" /></button>
        <div className="flex-1 h-2.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-[#2563EB] rounded-full transition-all" style={{ width: `${progress}%` }} /></div>
        <span className="text-xs font-bold text-slate-400">{idx + 1}/{screens.length}</span>
      </div>
      <div className="flex-1 px-5 pb-6 overflow-y-auto" data-testid="module-review-player">
        <StepView key={idx} step={step} onNext={next} play={play} playing={playing} ttsLoading={loading} ttsError={ttsError} lang={data.language} grade={grade} onSpoke={() => {}} />
      </div>
    </div>
  );
}
